import "bootstrap";
import { AnimatePresence } from "framer-motion";
import { Route, Routes, useLocation } from "react-router-dom";
import "./App.css";
import Auth from "./components/Auth/Auth";
import ForgotPassword from "./components/Auth/ForgotPassword";
import ForgotPasswordNewPassword from "./components/Auth/ForgotPasswordNewPassword";
import ForgotPasswordOTP from "./components/Auth/ForgotPasswordOTP";
import SignUp from "./components/Auth/SignUp";
import DigitalReceipt from "./components/History/DigitalReceipt";
import History from "./components/History/History";
import SingleTransaction from "./components/History/SingleTransaction";
import SingleTransactionRedemption from "./components/History/SingleTransactionRedemption";
import Home from "./components/Home/Home";
import Offers from "./components/Offer/Offers";
import SingleOffer from "./components/Offer/SingleOffer";
import MyProfile from "./components/Profile/MyProfile";
import Profile from "./components/Profile/Profile";
import Voucher from "./components/Voucher/Voucher";
import VoucherSingle from "./components/Voucher/VoucherSingle";

import { useEffect, useState } from "react";
import About from "./components/About/About";
import SingleAboutItemPage from "./components/About/SingleAboutItemPage";
import Alert from "./components/Auth/Alert/Alert";
import LoginSignUpWrapper from "./components/Auth/loginSignUpWrapper/LoginSignUpWrapper";
import GlobalBlocker from "./components/blocker/GlobalBlocker";
import NoAccess from "./components/Common/NoAccess";
import Feedback from "./components/Profile/Feedback";
import SharePoints from "./components/Profile/SharePoints";
import Stores from "./components/Stores/Store";
import TransferVoucher from "./components/Voucher/TransferVoucher";
import { injectAxiosAlertHandlers } from "./config/axiosInstance";
import { useAppSetting } from "./context/appSettingContext";
import { useFreeze } from "./context/useFreeze";
import usePingServer from "./context/usePingServer";
import { UserProvider } from "./context/userContext";
import useAlert from "./hooks/Alert";

function App() {
    const location = useLocation();
    const [toggleLoginTab, setToggleLoginTab] = useState("0");
    const { frozen, setFrozen } = useFreeze();
    usePingServer(frozen, setFrozen);
    const { appSettings, theme, clientAccess } = useAppSetting();

    const { setShowAlert, setAlertStyle, setAlertMessage, showAlert, alertStyle, alertMessage, hideAlert } = useAlert();

    const hasAccess = (accessType, accessList) => {
        return accessList.some((access) => access.access_type === accessType && access.enabled);
    };

    useEffect(() => {
        injectAxiosAlertHandlers({
            onMaintenance: () => {
                setFrozen(true);
            },
            onUnauthorized: () => {
                setFrozen(true);
            },
            onForbidden: () => {
                setFrozen(true);
            },
            onNotFound: () => {
                setFrozen(true);
            },
            onServerError: () => {
                setFrozen(true);
            },
        });
    }, []);

    useEffect(() => {
        const freshStartKey = "freshStartPerformed";

        async function freshStart() {
            try {
                console.log("Starting fresh...");

                // Unregister all service workers
                if ("serviceWorker" in navigator) {
                    const registrations = await navigator.serviceWorker.getRegistrations();
                    for (const registration of registrations) {
                        await registration.unregister();
                        console.log(`Unregistered service worker: ${registration.scope}`);
                    }
                }

                // Clear caches
                if ("caches" in window) {
                    const cacheNames = await caches.keys();
                    console.log("Available caches:", cacheNames);

                    for (const cacheName of cacheNames) {
                        const deleted = await caches.delete(cacheName);
                        console.log(`Cache ${cacheName} deleted: ${deleted}`);
                    }
                }

                // Clear IndexedDB
                const databases = await indexedDB.databases();
                for (const db of databases) {
                    await new Promise((resolve, reject) => {
                        const request = indexedDB.deleteDatabase(db.name);
                        request.onsuccess = () => {
                            console.log(`Deleted IndexedDB: ${db.name}`);
                            resolve();
                        };
                        request.onerror = (event) => {
                            console.error(`Error deleting IndexedDB: ${db.name}`, event);
                            reject(event);
                        };
                    });
                }

                console.log("Fresh start complete!");
            } catch (error) {
                console.error("Error during fresh start:", error);
            }
        }

        // Check if the fresh start script has already run
        if (!localStorage.getItem(freshStartKey)) {
            freshStart().then(() => {
                // Mark the fresh start as completed
                localStorage.setItem(freshStartKey, "true");
            });
        }
    }, []);

    function emitEvent() {
        console.log("Emitting 'trueValuePWA' event...");
        const event = new Event("trueValuePWA");
        window.dispatchEvent(event);
    }
    if (!appSettings || !clientAccess) {
        return <div>Loading...</div>; // Or a proper loader
    }

    if (appSettings !== undefined && clientAccess !== undefined) {
        const baseURL = appSettings?.AppBaseURL;
        const AppDisplayName = appSettings?.AppDisplayName;
        const ClientName = appSettings?.ClientName;
        const currency = appSettings?.Currency;

        const clientID = appSettings?.ClientId;

        if (baseURL !== undefined && clientID !== undefined && appSettings !== undefined) {
            return (
                <div className={"container-fluid w-100 position-relative px-0 font-family-dmsans bg-light max-width-700 position-relative " + (theme === "dark" ? "dark" : "")}>
                    {frozen && <GlobalBlocker setFrozen={setFrozen} message="🚧 App is under maintenance" />}
                    <div className="w-100 text-center position-fixed bg-black top-0 start-0 z-3 d-none" id="promptButton">
                        <button
                            className="btn btn-primary"
                            // onClick={() => emitEvent()}
                        >
                            Install App
                        </button>
                    </div>
                    <Alert showAlert={showAlert} alertStyle={alertStyle} alertMessage={alertMessage} hideAlert={hideAlert} alertTimer={10000} />
                    <AnimatePresence mode="wait">
                        <UserProvider>
                            <Routes key={location.pathname} location={location}>
                                <Route
                                    path="/"
                                    element={
                                        <Auth
                                            emitEvent={() => emitEvent()}
                                            appSettings={appSettings}
                                            AppDisplayName={AppDisplayName}
                                            baseURL={baseURL}
                                            clientID={clientID}
                                            activeTab={toggleLoginTab}
                                            setActiveTab={setToggleLoginTab}
                                        />
                                    }
                                ></Route>

                                <Route
                                    path="/login"
                                    element={
                                        <LoginSignUpWrapper baseURL={baseURL} clientID={clientID} appSettings={appSettings} AppDisplayName={AppDisplayName} activeTab={toggleLoginTab} setActiveTab={setToggleLoginTab} />
                                    }
                                ></Route>

                                <Route path="/forgot-password" element={<ForgotPassword appSettings={appSettings} baseURL={baseURL} clientID={clientID} AppDisplayName={AppDisplayName} />}></Route>

                                <Route path="/forgot-password-otp" element={<ForgotPasswordOTP appSettings={appSettings} baseURL={baseURL} clientID={clientID} AppDisplayName={AppDisplayName} />}></Route>

                                <Route path="/forgot-password-new-password" element={<ForgotPasswordNewPassword appSettings={appSettings} baseURL={baseURL} clientID={clientID} AppDisplayName={AppDisplayName} />}></Route>

                                {/* <Route
                                    path="/join"
                                    element={
                                        <SignUp
                                            baseURL={baseURL}
                                            clientID={clientID}
                                            appSettings={appSettings}
                                            AppDisplayName={AppDisplayName}
                                        />
                                    }
                                ></Route> */}
                                <Route path="/join" element={<SignUp baseURL={baseURL} clientID={clientID} appSettings={appSettings} AppDisplayName={AppDisplayName} />}></Route>

                                <Route
                                    path="/home"
                                    element={
                                        hasAccess("HO", clientAccess) ? (
                                            <Home
                                                // emitEvent={() => emitEvent()}
                                                appSettings={appSettings}
                                                AppDisplayName={AppDisplayName}
                                                currency={currency}
                                            />
                                        ) : (
                                            <NoAccess />
                                        )
                                    }
                                ></Route>

                                {/* <Route
                  path="/home/id"
                  element={
                    <HomeID
                      baseURL={baseURL}
                      clientID={clientID}
                      AppDisplayName={AppDisplayName}
                      currency = {currency}
                    />
                  } */}
                                {/* ></Route> */}

                                <Route
                                    path="/offers"
                                    element={
                                        hasAccess("OF", clientAccess) ? (
                                            <Offers
                                                // emitEvent={() => emitEvent()}
                                                baseURL={baseURL}
                                                clientID={clientID}
                                                appSettings={appSettings}
                                                AppDisplayName={AppDisplayName}
                                            />
                                        ) : (
                                            <NoAccess />
                                        )
                                    }
                                ></Route>

                                <Route path="/offers/single/:promoId/:promoCode" element={<SingleOffer baseURL={baseURL} clientID={clientID} appSettings={appSettings} AppDisplayName={AppDisplayName} />}></Route>

                                <Route
                                    path="/history"
                                    element={
                                        hasAccess("BH", clientAccess) ? (
                                            <History
                                                // emitEvent={() => emitEvent()}
                                                baseURL={baseURL}
                                                clientID={clientID}
                                                AppDisplayName={AppDisplayName}
                                                currency={currency}
                                            />
                                        ) : (
                                            <NoAccess />
                                        )
                                    }
                                ></Route>

                                <Route
                                    path="/transactions/single/:billref"
                                    element={<SingleTransaction baseURL={baseURL} clientID={clientID} appSettings={appSettings} AppDisplayName={AppDisplayName} ClientName={ClientName} currency={currency} />}
                                ></Route>

                                <Route
                                    path="/transactions/single/redemption/:billref"
                                    element={<SingleTransactionRedemption baseURL={baseURL} clientID={clientID} AppDisplayName={AppDisplayName} currency={currency} />}
                                ></Route>

                                <Route path="/digital-receipt" element={<DigitalReceipt AppDisplayName={AppDisplayName} />}></Route>

                                <Route
                                    path="/profile"
                                    element={
                                        hasAccess("PR", clientAccess) ? (
                                            <Profile
                                                // emitEvent={() => emitEvent()}
                                                baseURL={baseURL}
                                                clientID={clientID}
                                                appSettings={appSettings}
                                                AppDisplayName={AppDisplayName}
                                                currency={currency}
                                            />
                                        ) : (
                                            <NoAccess />
                                        )
                                    }
                                ></Route>

                                <Route path="/my-profile" element={<MyProfile baseURL={baseURL} clientID={clientID} appSettings={appSettings} AppDisplayName={AppDisplayName} />}></Route>

                                <Route
                                    path="/vouchers"
                                    element={hasAccess("VC", clientAccess) ? <Voucher currency={currency} baseURL={baseURL} clientID={clientID} AppDisplayName={AppDisplayName} /> : <NoAccess />}
                                ></Route>
                                <Route path="/vouchers/single" element={<VoucherSingle AppDisplayName={AppDisplayName} />}></Route>

                                <Route path="/vouchers/single/transfer" element={<TransferVoucher AppDisplayName={AppDisplayName} />}></Route>

                                <Route path="/share-points" element={<SharePoints currency={currency} baseURL={baseURL} clientID={clientID} AppDisplayName={AppDisplayName} />}></Route>

                                <Route path="/feedback" element={<Feedback baseURL={baseURL} clientID={clientID} AppDisplayName={AppDisplayName} />}></Route>

                                <Route path="/stores" element={<Stores appSettings={appSettings} AppDisplayName={AppDisplayName} />}></Route>

                                <Route path="/about" element={<About appSettings={appSettings} AppDisplayName={AppDisplayName} />}></Route>
                                <Route path="/about/single" element={<SingleAboutItemPage appSettings={appSettings} AppDisplayName={AppDisplayName} />}></Route>
                            </Routes>
                        </UserProvider>
                    </AnimatePresence>
                </div>
            );
        } else {
            return <>{frozen && <GlobalBlocker setFrozen={setFrozen} message="🚧 App is under maintenance" />}</>;
        }
    } else {
        return <>{frozen && <GlobalBlocker setFrozen={setFrozen} message="🚧 App is under maintenance" />}</>;
    }
}

export default App;
